import { useMemo } from 'react';
import { useTeamPaths } from './useTeamPaths';

interface ConfigOptions {
  allowedOrganizations: string[];
  allowedHosts: string[];
  allowedNamespaces: string[];
  projectTypes: string[];
  defaultOrganization: string;
  defaultHost: string;
  defaultNamespace: string;
  defaultProjectType: string;
  usesDynamicNamespaces: boolean;
  namespacesLoading: boolean;
  namespacesError?: Error;
}

interface ConfigValidation {
  isValid: boolean;
  errors: string[];
}

/**
 * Hook to extract and normalize configuration options from uiSchema
 * Support dynamic namespaces via catalog when not provided hardcoded
 */
export const useConfigOptions = (uiSchema: any): ConfigOptions => {
  const {
    teamPaths,
    loading: namespacesLoading,
    error: namespacesError,
  } = useTeamPaths();

  return useMemo(() => {
    const options = uiSchema?.['ui:options'] || {};

    // Check if dynamic namespaces should be used
    const shouldUseDynamicNamespaces =
      !options.allowedNamespaces || options.allowedNamespaces.length === 0;

    const normalizeOptions = (
      opts: string[] = [],
      defaultValue = '',
    ): string[] => {
      return Array.from(
        new Set([defaultValue, ...opts].filter(option => option.length > 0)),
      ).sort();
    };

    const allowedOrganizations = normalizeOptions(
      options.allowedOrganizations,
      options.defaultOrganization,
    );

    const allowedHosts = normalizeOptions(
      options.allowedHosts,
      options.defaultHost,
    );

    // Use dynamic team paths or hardcoded configuration
    const namespacesSource = shouldUseDynamicNamespaces
      ? teamPaths
      : options.allowedNamespaces;
    const allowedNamespaces = normalizeOptions(
      namespacesSource,
      options.defaultNamespace,
    );

    const projectTypes = normalizeOptions(
      options.projectTypes,
      options.defaultProjectType,
    );

    return {
      allowedOrganizations,
      allowedHosts,
      allowedNamespaces,
      projectTypes,
      defaultOrganization:
        options.defaultOrganization || allowedOrganizations[0],
      defaultHost: options.defaultHost || allowedHosts[0],
      defaultNamespace: options.defaultNamespace || allowedNamespaces[0],
      defaultProjectType: options.defaultProjectType || projectTypes[0],
      usesDynamicNamespaces: shouldUseDynamicNamespaces,
      namespacesLoading,
      namespacesError,
    };
  }, [namespacesError, namespacesLoading, teamPaths, uiSchema]);
};

/**
 * Hook to validate configuration
 */
export const useConfigValidation = (
  config: ConfigOptions,
): ConfigValidation => {
  return useMemo(() => {
    const errors: string[] = [];

    if (config.allowedOrganizations.length === 0) {
      errors.push(
        'allowedOrganizations ou defaultOrganization deve ser configurado no template',
      );
    }

    if (config.allowedHosts.length === 0) {
      errors.push(
        'allowedHosts ou defaultHost deve ser configurado no template',
      );
    }

    // Special validation for dynamic namespaces
    if (config.usesDynamicNamespaces) {
      if (config.namespacesError) {
        errors.push(
          `Erro ao buscar team paths: ${config.namespacesError.message}`,
        );
      } else if (
        !config.namespacesLoading &&
        config.allowedNamespaces.length === 0
      ) {
        errors.push(
          'Nenhum team path encontrado no catálogo. Verifique se existem grupos com a anotação gitlab.com/team-path',
        );
      }
    } else if (config.allowedNamespaces.length === 0) {
      errors.push(
        'allowedNamespaces ou defaultNamespace deve ser configurado no template',
      );
    }

    if (config.projectTypes.length === 0) {
      errors.push(
        'projectTypes ou defaultProjectType deve ser configurado no template',
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [config]);
};
